/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import ImageModal from "components/ImageModal";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteWitness,
  fetchWitnessByIncidentRegistrationNumber,
} from "services/lealIntityService";
import {
  CrimeReference,
  FILE_URL,
  Investigation,
  PreliminaryInquiry,
  Witness,
} from "types";
import { deleteIcon, editIcon, viewIcon } from "utils/constant";
import { constructFileUrl, errorHandler } from "utils/helper";
import WitnessDetail from "./modals/WitnessDetail";
import WitnessForm from "./modals/WitnessForm";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident?: CrimeReference;
  canManageIncidentDetail: boolean;
};

const WitnessesTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [preliminaries, setPreliminaries] = useState<PreliminaryInquiry[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [loading, setLoading] = React.useState(true);
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedWitness, setSelectedWitness] = React.useState<Witness>();
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);

  const fetchWitnessData = async () => {
    setLoading(true);
    fetchWitnessByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then(setWitnesses)
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWitnessData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">👁️</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("witness_information")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_witnesses_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <ActionButton
              onClick={() => {
                setAction("Add");
                setIsOpenForm(true);
              }}
            >
              ➕ {t("add_witness")}
            </ActionButton>
          )}
        </div>
      </div>

      {/* Witnesses List */}
      {witnesses.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_witnesses_found")}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {witnesses.map((witness, index) => {
            const preliminary = preliminaries?.find((item) =>
              item?.witness_list?.find(
                (w) => w?.witness_info_detail?.uu_id === witness?.uu_id,
              ),
            );

            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img
                      src={(() => {
                        const doc = witness?.documents?.find(
                          (doc) => doc.document_type === "witness_picture",
                        );
                        return doc
                          ? constructFileUrl(doc, FILE_URL.WITNESS)
                          : require("../../../images/imageVector.png");
                      })()}
                      alt="Witness"
                      className="w-12 h-12 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setSelectedWitness(witness);
                        setIsImageModalOpen(true);
                      }}
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {`${witness?.first_name} ${witness?.middle_name} ${witness?.last_name}`}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <span>
                          {witness?.age} {t("years_old_label")}
                        </span>
                        <span>•</span>
                        <span>{witness?.education_level}</span>
                        <span>•</span>
                        <span>{witness?.nationality}</span>
                        {preliminary && (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            {t("preliminary")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedWitness({ ...witness, preliminary });
                        setIsOpenDetail(true);
                      }}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                      title={t("view_details_button")}
                    >
                      {viewIcon}
                    </button>
                    {canManageIncidentDetail &&
                      incident?.status !== "forwarded" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setAction("Edit");
                            setSelectedWitness(witness);
                            setIsOpenForm(true);
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_witness")}
                        >
                          {editIcon}
                        </button>
                      )}
                    {canManageIncidentDetail &&
                      incident?.status !== "forwarded" && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWitness(witness);
                            setIsOpenDelete(true);
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_witness")}
                        >
                          {deleteIcon}
                        </button>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {isOpenForm && (
        <WitnessForm
          action={action}
          investigation={investigation}
          incident={incident}
          witness={action === "Edit" ? selectedWitness : undefined}
          isOpen={isOpenForm}
          onClose={(refetch) => {
            setIsOpenForm(false);
            if (refetch) fetchWitnessData();
          }}
        />
      )}
      <WitnessDetail
        witness={selectedWitness}
        investigation={investigation}
        allPreliminaries={preliminaries}
        isOpen={isOpenDetail}
        onClose={(refetch) => {
          setIsOpenDetail(false);
          if (refetch) {
            fetchWitnessData();
          }
        }}
      />
      {selectedWitness && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteWitness(selectedWitness)
              .then(() => {
                showTooltip(t("successfully_deleted"), "success");
                fetchWitnessData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail ||
                    errorHandler(error) ||
                    t("something_went_wrong_try_again"),
                  "error",
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title={t("delete_witness")}
          message={t("confirm_delete_witness", {
            name: `${selectedWitness?.first_name || ""} ${selectedWitness?.middle_name || ""} ${selectedWitness?.last_name || ""}`.trim(),
          })}
        />
      )}
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={(() => {
          const doc = selectedWitness?.documents?.find(
            (doc) => doc.document_type === "witness_picture",
          );
          return doc
            ? constructFileUrl(doc, FILE_URL.WITNESS)
            : require("../../../images/imageVector.png");
        })()}
        label={
          selectedWitness?.first_name +
          " " +
          selectedWitness?.middle_name +
          " " +
          selectedWitness?.last_name
        }
        onClose={() => setIsImageModalOpen(false)}
      />
    </div>
  );
};

export default WitnessesTab;
